import { ITaxInOffer, ITaxWithheld } from "@modules/taxes/domain/entities/taxes.model";
import { Fiat } from "./asset/fiat/fiat";
import { Time } from "./time";
import { SELECTED_CURRENCY, TRADE_TYPE, TradeOfferModel } from "./trade-offer.model";
import { AmountDefinedFor, BonusType, CreateOfferMutation, CreateOfferRequest, TradeType } from "@gql/offer";
import { UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";

export interface IFeeInOffer {
	fee: Partial<IFee> | null;
	isFeeTransparent: boolean;
}

export interface IFee {
	fiat: string;
	asset: string;
	net: string;
	percentage: string;
	priceWithoutFee: string;
}

export interface IVSNFeeTokenCollection {
	globalOptIn?: boolean;
	enabled: boolean;
	eligibleForVsn: boolean;
	feeDiscount: string;
	feeDiscountBefore?: string;
	visionFeeDetail?: IVisionFeeDetail | null;
}

export interface IVisionFeeDetail {
	visionRate: string;
	visionAmount: string;
	feeBefore: string;
	hasEnoughFunds: boolean;
	bonus: IVisionFeeBonus;
}

export interface IVisionFeeBonus {
	amount: string;
	unitId: string;
	type: BonusType;
}

export class Offer extends TradeOfferModel {
	private wallet_id: string = "";
	private fiat_wallet_id: string = "";
	private amount_fiat: string = "";
	private amount_cryptocoin: string = "";
	private amount_fiat_bfc: string = "";
	private amount_cryptocoin_bfc: string = "";
	private expires_at: Time = new Time();
	private amount_defined_for: SELECTED_CURRENCY = null;
	private price: string = "";
	private fiat_id: string = "";
	private cryptocoin_id: string = "";
	private directTradableWalletOffer: boolean = false;
	private directBestOffer: boolean = false;
	private useIndexTrade: boolean = false;
	private price_bfc: string = "";
	private sell_all: boolean = false;
	private current_borrow: string = "";
	private future_borrow: string = "";
	private effective_leverage: string = "";
	private tax: ITaxInOffer = null;
	private user_journey: UserJourney = null;
	private fee: IFeeInOffer = null;
	private priceWithoutFee: string = "";
	private visionBonus: boolean = null;
	private vsnFeeTokenCollection: IVSNFeeTokenCollection = null;
	private countDown: number = 0;

	public setFiatWalletId(id: string): void {
		this.fiat_wallet_id = id;
	}

	public getFiatWalletId(): string {
		return this.fiat_wallet_id;
	}

	public isDirectBestOffer(): boolean {
		return this.directBestOffer;
	}

	public setDirectBestOffer(b: boolean): void {
		this.directBestOffer = b;
	}

	public setAmountAssetBfc(s: string): void {
		this.amount_cryptocoin_bfc = s;
	}

	public getAmountAssetBfc(): string {
		return this.amount_cryptocoin_bfc;
	}

	public setAmountFiatBfc(s: string): void {
		this.amount_fiat_bfc = s;
	}

	public getAmountFiatBfc(): string {
		return this.amount_fiat_bfc;
	}

	public setPriceBfc(s: string): void {
		this.price_bfc = s;
	}

	public getPriceBfc(): string {
		return this.price_bfc;
	}

	public setUserWants(s: SELECTED_CURRENCY): void {
		this.selected_currency = s;
		this.setAmountDefinition(s === SELECTED_CURRENCY.FIAT ? SELECTED_CURRENCY.CRYPTOCOIN : SELECTED_CURRENCY.FIAT);
	}

	public getAmountDefinition(): SELECTED_CURRENCY {
		return this.amount_defined_for;
	}

	public setAmountDefinition(s: SELECTED_CURRENCY): void {
		this.amount_defined_for = s;
	}

	public getExpiration(): number {
		return this.expires_at.getUnix();
	}

	public getExpirationFull(): Time {
		return this.expires_at;
	}

	public setAmountFiat(num: string): void {
		this.amount_fiat = num;
	}

	public getAmountFiat(): string {
		return this.amount_fiat;
	}

	public setAmountTradable(num: string): void {
		this.amount_cryptocoin = num;
	}

	public getAmountTradable(): string {
		return this.amount_cryptocoin;
	}

	public getWalletId(): string {
		return this.wallet_id;
	}

	public setWalletId(id: string): void {
		this.wallet_id = id;
	}

	public setExpiration(expirationFull: Time): void {
		this.expires_at = expirationFull;
	}

	public getPrice(): number {
		return +this.price;
	}

	public setPrice(price: number): void {
		this.price = price + "";
	}

	public getUserJourney(): UserJourney {
		return this.user_journey;
	}

	public setUserJourney(userJourney: string): void {
		this.user_journey = userJourney as UserJourney;
	}

	public setDirectTradableWalletOffer(b: boolean): void {
		this.directTradableWalletOffer = b;
	}

	public getDirectTradableWalletOffer(): boolean {
		return this.directTradableWalletOffer;
	}

	public stepBack(): void {
		delete this["amount"];
		if (this.fiat_wallet_id === "") {
			this.wallet_id = "";
		}
		this.amount_cryptocoin = "";
		this.amount_fiat = "";
		this.fiat_wallet_id = "";
	}

	public setCurrencyId(tradableId: string): void {
		this.cryptocoin_id = tradableId;
	}

	public isIndexTrade(): boolean {
		return this.useIndexTrade;
	}

	public setIndexTrade(b: boolean): void {
		this.useIndexTrade = b;
	}

	public getCurrencyId(): string {
		return this.cryptocoin_id;
	}

	public setFiat(f: Fiat): void {
		this.fiat_id = f.getId();
	}

	public getFiatId(): string {
		return this.fiat_id;
	}

	public setFiatId(s: string): void {
		this.fiat_id = s;
	}

	public getSellAll(): boolean {
		return this.sell_all;
	}

	public setSellAll(sellAll: boolean): void {
		this.sell_all = sellAll;
	}

	public getCurrentBorrow(): string {
		return this.current_borrow;
	}

	public getFutureBorrow(): string {
		return this.future_borrow;
	}

	public getEffectiveLeverage(): string {
		return this.effective_leverage;
	}

	public setCurrentBorrow(s: string): void {
		this.current_borrow = s;
	}

	public setFutureBorrow(s: string): void {
		this.future_borrow = s;
	}

	public setEffectiveLeverage(s: string): void {
		this.effective_leverage = s;
	}

	public getTax(): ITaxInOffer {
		return this.tax;
	}

	public setTax(tax: ITaxInOffer): void {
		this.tax = tax;
	}

	public getFee(): IFeeInOffer {
		return this.fee;
	}

	public setFee(fee: IFeeInOffer): void {
		this.fee = fee;
	}

	public getVisionBonus(): boolean {
		return this.visionBonus;
	}

	public setVisionBonus(flag: boolean): void {
		this.visionBonus = flag;
	}

	public getVsnFeeTokenCollection(): IVSNFeeTokenCollection {
		return this.vsnFeeTokenCollection;
	}

	public setVsnFeeTokenCollection(vsnFeeTokenCollection: IVSNFeeTokenCollection): void {
		this.vsnFeeTokenCollection = vsnFeeTokenCollection;
	}

	public getCountDown(): number {
		return this.countDown;
	}
	public setCountDown(countDown: number): void {
		this.countDown = countDown;
	}
}

const tradeTypeToGql = {
	[TRADE_TYPE.BUY]: TradeType.Buy,
	[TRADE_TYPE.SELL]: TradeType.Sell
};

const tradeTypeToDomain = {
	[TradeType.Buy]: TRADE_TYPE.BUY,
	[TradeType.Sell]: TRADE_TYPE.SELL
};

const amountDefinedFor = {
	[SELECTED_CURRENCY.CRYPTOCOIN]: AmountDefinedFor.Assets,
	[SELECTED_CURRENCY.FIAT]: AmountDefinedFor.Fiat
};

const deserializeOfferExpirationGql = (iso8601: string): Time => {
	const expiration = new Time();
	expiration.setByDate(new Date(iso8601));
	return expiration;
};

export const getOfferAmount = (offer: Offer): string =>
	offer.getAmountDefinition() === SELECTED_CURRENCY.CRYPTOCOIN ? offer.getAmountTradable() : offer.getAmountFiat();

/**
 * Serialises offer DTO to a request that GQL API expects
 *
 * @param offer - offer DTO
 * @returns - POJO in a form that is expected from the API
 */
export const serializeOfferGql = (offer: Offer): CreateOfferRequest => ({
	amount: { value: getOfferAmount(offer) },
	amountDefinedFor: amountDefinedFor[offer.getAmountDefinition()],
	assetId: offer.getCurrencyId(),
	fiatId: offer.getFiatId(),
	type: tradeTypeToGql[offer.getType()],
	visionBonus: offer.getVisionBonus()
});

/**
 * Converts the offer received from the API to the client side DTO
 *
 * @param offer - offer received from the API
 * @param offerData - DTO - this object is updated with the latest offer info from the API and returned
 * @returns - updated offer DTO - object that has the latest offer data from the API and relevant UI state
 */
export const deserializeOfferGql = ({ createOffer: { offer } }: CreateOfferMutation, offerData: Offer): Offer => {
	const {
		offerId,
		amountAsset,
		amountFiat,
		type,
		price,
		expiresAt,
		warnings,
		currentBorrow,
		futureBorrow,
		effectiveLeverage,
		tax,
		isTaxable,
		userJourney,
		fee,
		isFeeTransparent,
		vsnFeeTokenCollection,
		serverTime
	} = offer;

	const newOffer = Object.assign(Object.create(Object.getPrototypeOf(offerData)), offerData);
	const expiration = deserializeOfferExpirationGql(expiresAt.iso8601);

	let _tax: Partial<ITaxWithheld> = null;
	let _isTaxable: boolean = false;
	let _fee: Partial<IFee> = null;
	let _isFeeTransparent: boolean = false;
	let _vsnFeeTokenCollection: IVSNFeeTokenCollection = null;

	// only fully filled offers have FEE
	if (fee) {
		const { fiat, asset, net, percentage, priceWithoutFee } = fee;
		_fee = {
			fiat: fiat?.value,
			asset: asset?.value,
			net: net?.value,
			percentage: percentage?.value,
			priceWithoutFee: priceWithoutFee?.value
		};
	}

	if (isFeeTransparent) {
		_isFeeTransparent = isFeeTransparent;
	}

	// only fully filled offers have TAX
	if (tax) {
		_tax = {
			assetId: tax?.assetId,
			fiatId: tax?.fiatId,
			isTaxServiceAvailable: tax?.isTaxServiceAvailable,
			taxAmountAsset: tax?.taxAmountAsset?.value,
			taxAmountFiat: tax?.taxAmountFiat.value,
			fiatAmountAfterTax: tax?.fiatAmountAfterTax?.value
		};
	}

	if (isTaxable) {
		_isTaxable = isTaxable;
	}

	const combinedTax: ITaxInOffer = {
		isTaxable: _isTaxable,
		tax: _tax
	};

	const combineFee: IFeeInOffer = {
		fee: _fee,
		isFeeTransparent: _fee ? _isFeeTransparent : false
	};

	// only fully filled offers have VSN FEE
	if (vsnFeeTokenCollection) {
		const { globalOptIn, enabled, eligibleForVsn, feeDiscount, visionFeeDetail } = vsnFeeTokenCollection;
		_vsnFeeTokenCollection = {
			globalOptIn,
			enabled,
			eligibleForVsn,
			feeDiscount,
			visionFeeDetail
		};
	}

	//countdown
	const _countdown = expiresAt.unixTimestamp - serverTime.unixTimestamp;

	newOffer.setId(offerId);
	newOffer.setAmountTradable(amountAsset?.value);
	newOffer.setAmountFiat(amountFiat?.value);
	newOffer.setPrice(+price?.value);
	newOffer.setUserJourney(userJourney);
	newOffer.setType(tradeTypeToDomain[type]);
	newOffer.setExpiration(expiration);
	newOffer.setStockWarnings(warnings);
	newOffer.setCurrentBorrow(currentBorrow?.value);
	newOffer.setFutureBorrow(futureBorrow?.value);
	newOffer.setEffectiveLeverage(effectiveLeverage?.value);
	newOffer.setTax(combinedTax);
	newOffer.setFee(combineFee);
	newOffer.setVsnFeeTokenCollection(_vsnFeeTokenCollection);
	newOffer.setCountDown(_countdown);

	return newOffer;
};
